import { Link as LinkIcon, Upload, Video as VideoIcon, Youtube } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useVideoSystem, type VideoType } from '../../hooks/useVideoSystem';
import { supabase } from '../../lib/supabase';

interface VideoForm {
  title: string;
  description: string;
  url: string;
}

const initialForm: VideoForm = {
  title: '',
  description: '',
  url: ''
};

export function VideoUploader() {
  const { user } = useAuth();
  const { chatbotPublicId } = useChatbot();
  const { addVideo, loading, error } = useVideoSystem();
  const [form, setForm] = useState<VideoForm>(initialForm);
  const [activeTab, setActiveTab] = useState<'url' | 'upload'>('url');

  const extractVideoInfo = (url: string): { type: VideoType; videoId: string } | null => {
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return { type: 'youtube', videoId: youtubeMatch[1] };
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return { type: 'vimeo', videoId: vimeoMatch[1] };
    }

    // Dailymotion
    const dailymotionRegex = /(?:dailymotion\.com\/(?:video|embed\/video)\/|dai\.ly\/)([a-zA-Z0-9]+)/;
    const dailymotionMatch = url.match(dailymotionRegex);
    if (dailymotionMatch) {
      return { type: 'dailymotion', videoId: dailymotionMatch[1] };
    }

    return null;
  };

  const getThumbnailUrl = (type: VideoType, videoId: string): string => {
    switch (type) {
      case 'youtube':
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      case 'vimeo':
        // Note: Vimeo nécessite une API pour obtenir la miniature
        return '';
      case 'dailymotion':
        return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
      default:
        return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.url || !form.title || !chatbotPublicId) return;

    try {
      const videoInfo = extractVideoInfo(form.url);
      await addVideo(chatbotPublicId, {
        title: form.title,
        description: form.description,
        url: form.url,
        type: videoInfo ? videoInfo.type : 'upload',
        videoId: videoInfo?.videoId
      });

      setForm(initialForm);
    } catch (err) {
      console.error('Error adding video:', err);
    }
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    if (!file.type.startsWith('video/')) {
      // TODO: Afficher une erreur pour type de fichier invalide
      return;
    }

    try {
      // Upload du fichier vers le storage
      const fileExt = file.name.split('.').pop();
      if (!chatbotPublicId) {
        throw new Error('Chatbot ID is missing');
      }
      
      const filePath = `videos/${chatbotPublicId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Récupérer l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      // Créer la vidéo avec l'URL du fichier uploadé
      await addVideo(chatbotPublicId, {
        title: file.name.replace(`.${fileExt}`, ''),
        description: '',
        url: publicUrl,
        type: 'upload'
      });
    } catch (err) {
      console.error('Error uploading video:', err);
    }
  }, [user, addVideo]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('url')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'url'
              ? 'bg-indigo-500/20 text-indigo-400'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          <LinkIcon className="w-4 h-4" />
          URL
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'upload'
              ? 'bg-indigo-500/20 text-indigo-400'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          <Upload className="w-4 h-4" />
          Upload
        </button>
      </div>

      {activeTab === 'url' ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Video URL
            </label>
            <div className="relative">
              <input
                type="url"
                value={form.url}
                onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://youtube.com/watch?v=..."
              />
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Video Title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description (optional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Video Description"
              rows={3}
            />
          </div>

          <button
            type="submit"
            disabled={loading.upload || !form.url.trim() || !form.title.trim()}
            className="w-full bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading.upload ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <VideoIcon className="w-4 h-4" />
                Add the video
              </>
            )}
          </button>
        </form>
      ) : (
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 transition-colors ${
            loading.upload
              ? 'opacity-50'
              : 'border-white/10 hover:border-indigo-500/50'
          }`}
        >
          <input
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={loading.upload}
          />

          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-400">
              Drag and drop a video here or click to select
            </p>
            <p className="mt-1 text-xs text-gray-500">
              MP4, WebM up to 100MB
            </p>
          </div>

          {loading.upload && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
              <div className="text-white text-sm flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Uploading...
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}